import L from "leaflet";
import type { Polygon, Position } from "geojson";
import { serviceProvider } from "../../../../../comun/provider/ServiceProvider";
import { ConfirmModalRender } from "../../../../implementations/modal/ConfirmModalRender";
import { AoiDrawEngine } from "./AoiDrawEngine";
import { LoteSplitLineEngine } from "./LoteSplitLineEngine";
import { LoteSplitState } from "./LoteSplitState";
// El padre es la unión de sus lotes: la misma función con la que
// `RegistroFincaPaso4Render` lo recalcula y lo guarda.

/** Herramienta activa del paso 4. */
export type LoteSplitHerramienta =
  | "dividir"
  | "unir"
  | "dibujar"
  | "editar"
  | "borrar";

/** Aviso que se muestra sobre el mapa según la herramienta activa — mismo
 *  recurso que el paso 3 (`AoiMapRender`), que a su vez lo toma de la app de
 *  referencia externa (`BarraDibujo.tsx`, constante `AVISOS`). */
export const AVISOS_PASO4: Record<LoteSplitHerramienta, string> = {
  dividir: "Toque un lote para dividirlo en lotes.",
  unir: "Seleccione dos lotes consecutivos.",
  editar: "Toque un lote para editar sus vértices.",
  borrar: "Seleccione el lote que quiere borrar.",
  dibujar:
    "De Click en el mapa para dibujar el lote nuevo. Si empieza y termina sobre " +
    "un vértice del lote padre, el contorno se completa solo.",
};

/** Aviso del modo Dividir una vez que el usuario YA eligió el lote: desde ahí
 *  los clicks son del trazo. Son dos pasos y por lo tanto dos avisos — con uno
 *  solo, "dibuje una línea" aparecía antes de que hubiera un lote elegido y el
 *  primer click no hacía lo que el texto prometía. */
export const AVISO_DIVIDIR_TRAZANDO =
  "Dibuje la línea de corte sobre el lote.";

/** Aviso del modo Editar una vez que el lote ya está elegido. Dos pasos, dos
 *  avisos, por lo mismo que en Dividir: con uno solo, "arrastre un vértice"
 *  aparecía antes de que hubiera un lote del que arrastrarlo. */
export const AVISO_EDITAR_VERTICES =
  "Arrastre un vértice para moverlo, el intermedio para agregarlo, o haga clic en uno para quitarlo.";

/** Contorno del lote que se está dividiendo.
 *
 *  **Es la única geometría de lote que Leaflet dibuja en el paso 4**, y es a
 *  propósito: se dibuja con el polígono que acaba de leerse por gRPC, o sea con
 *  el mismo contra el que snapea la línea y el mismo sobre el que el servidor va
 *  a aplicar el corte. Que el resalte salga de ahí y no de un GetMap aparte es
 *  lo que garantiza que lo que se ve marcado es exactamente lo que se va a
 *  cortar — y además aparece sin esperar otra petición, porque la geometría ya
 *  está en la mano.
 *
 *  `fill: false` no es un detalle: el relleno del lote ya lo pinta el WMS, y
 *  superponerle otro daría una opacidad sumada — el mismo error que el paso 3
 *  tuvo cuando Leaflet y el WMS dibujaban las dos la misma pieza.
 *
 *  El ancho es el MISMO que el del estilo de reposo (3 px, el `stroke-width` de
 *  `sextante_lote_temporal.sld`): lo que distingue al lote elegido es el color,
 *  no el grosor. Los resaltes de unir/borrar sí engordan el borde, porque ahí
 *  lo que está en juego es que el lote desaparezca. */
const CONTORNO_A_DIVIDIR: L.PathOptions = {
  // Azul aguamarina. No es ninguno de los colores que ya significan algo en
  // este mapa: el naranja es el lote en reposo, el amarillo es seleccionado o
  // elegido, el rojo es borrar y el verde es un vértice que se puede agarrar.
  color: "#40E0D0",
  weight: 3,
  opacity: 1,
  fill: false,
  interactive: false,
};

/** Toolbar del paso 4: **Dividir** y **Unir**, con comportamiento tipo radio
 *  (activar una apaga la otra).
 *
 *  **Dividir** traza la línea de corte con motor propio
 *  (`LoteSplitLineEngine`), no con `L.Draw.Polyline`: misma metodología
 *  manual que el paso 3 y que la app de referencia externa. Con eso este
 *  archivo dejó de depender de `leaflet-draw`, que era el último consumidor
 *  del plugin en el proyecto. Si el corte falla (la línea no llegó a cruzar
 *  ninguna pieza de lado a lado, o se saldría de su contorno) se avisa por el
 *  `errorService` — el control original lo dejaba en silencio, acá se
 *  prefiere avisar.
 *
 *  **Unir** rehace lo contrario: dos clicks sobre lotes ADYACENTES los
 *  fusionan en uno, previa validación geométrica
 *  —que decide el SERVIDOR, en el rpc `UnirLotes`— y confirmación explícita. La
 *  selección la maneja `LoteSplitMapRender`, que resuelve el click contra el
 *  modelo; acá solo se enciende el modo y se avisa por `onHerramienta`.
 *  A diferencia de la fusión que hubo —que solo aceptaba las dos mitades de un mismo
 *  corte— vale cualquier par de piezas que se toquen.
 *
 *  **Editar** arrastra los vértices del lote bajo el cursor, llevándose los de
 *  los lotes vecinos que comparten cada punto (`LoteVertexEditEngine`). Acá
 *  solo se enciende el modo: el motor lo maneja `LoteSplitMapRender`, que es
 *  quien tiene las capas — mismo reparto que el modo Unir.
 *
 *  **Dibujar** traza un lote nuevo de cero, con el MISMO motor del paso 3
 *  (`AoiDrawEngine` en modo `draw`): misma mecánica de clicks, misma guía en
 *  vivo, mismas reglas de trazo. Acá además va **con snap** a los bordes de los
 *  lotes existentes, que en el paso 3 no aplica: un lote nuevo que se apoya en
 *  sus vecinos tiene que compartir sus vértices EXACTOS, o queda una franja de
 *  solape que el guardado final rechaza. Al cerrarse el polígono se saca de ese motor
 *  y entra al estado como un lote más — el motor solo dibuja, los lotes los
 *  tiene `LoteSplitState`. Puede quedar FUERA de los polígonos padre: el padre
 *  se recalcula desde los lotes, así que dibujar afuera lo agranda.
 *
 *  **Borrar** saca un lote del registro. Un click lo elige y pide confirmación
 *  (igual que Unir, y por el mismo motivo que el Borrar del paso 3: evitar
 *  borrados accidentales). El área que ocupaba deja de ser parte del predio —
 *  el polígono padre se recalcula desde los lotes que quedan. */
/** Cuánto se espera, como mucho, a que vuelvan los lotes de un corte antes de
 *  volver a habilitar la elección de lote (ver
 *  `LoteSplitToolbar._marcarCorteEnVuelo`). Lo normal es que el cambio de
 *  estado lo libere mucho antes; esto sólo cubre el corte que falla. */
const ESPERA_MAXIMA_CORTE_MS = 10000;

export class LoteSplitToolbar {
  /** Registra los botones y arma el motor de la línea de corte.
   *
   *  Hasta el 2026-10-27 recibía además el `registroId`, que sólo servía para
   *  leer de la base el lote a dividir (ver `comenzarCorteDeAsync`). */
  init(
    parent: HTMLElement,
    map: L.Map,
    state: LoteSplitState,
  ): void {
    // Se guarda para poder pintar el contorno del lote a dividir (ver
    // `CONTORNO_A_DIVIDIR`); antes el mapa sólo se usaba acá, para armar los
    // motores.
    this._mapa = map;
    this._state = state;
    this._engine = new LoteSplitLineEngine({
      map,
      onLineaTrazada: this._onLineaTrazada,
    });

    // Los handlers van por sus campos nombrados, NO por arrows inline: si no,
    // el `L.DomEvent.off` de `dispose()` no tendria la misma referencia y no
    // quitaria nada.
    this._splitBtn = this._makeButton("ph-scissors", "Dividir", this._onSplitClick);
    this._unirBtn = this._makeButton("ph-intersect", "Unir", this._onUnirClick);
    // Mismo ícono que el botón "Editar" del paso 3: es la misma acción
    // (arrastrar vértices), sobre lotes en vez de sobre el perímetro.
    this._editarBtn = this._makeButton(
      "ph-pencil-simple",
      "Editar",
      this._onEditarClick,
    );
    // Mismo ícono que el botón "Borrar" del paso 3.
    this._borrarBtn = this._makeButton("ph-trash", "Borrar", this._onBorrarClick);
    // Mismo ícono que el botón "Dibujar" del paso 3, que es el mismo motor.
    this._dibujarBtn = this._makeButton(
      "ph-polygon",
      "Dibujar",
      this._onDibujarClick,
    );
    // Orden pedido: Dividir, Unir, Dibujar, Editar, Borrar.
    parent.appendChild(this._splitBtn);
    parent.appendChild(this._unirBtn);
    parent.appendChild(this._dibujarBtn);
    parent.appendChild(this._editarBtn);
    parent.appendChild(this._borrarBtn);

    // El motor de dibujo se arma acá pero arranca apagado: activarlo es lo que
    // engancha sus eventos de puntero al mapa (ver `AoiDrawEngine.activar`).
    this._engineDibujo = new AoiDrawEngine({
      map,
      // El lote NO se arma acá: lo que se emite son los puntos clickeados y la
      // geometría la decide el servidor (`DibujarLote`), igual que en el corte,
      // la unión y los vértices.
      onTrazo: this._onTrazoDeLote,
      // El autocierre no lo decide el motor ni este cliente: lo decide el
      // servidor y lo confirma el usuario (ver `_onAutocierrePosible`).
      onAutocierrePosible: this._onAutocierrePosible,
      // Pegado a los VÉRTICES que ya existen, con el handle verde del modo
      // Editar: un lote nuevo tiene que apoyarse en el mismo punto que el
      // vecino, no en uno cercano, y es la condición del autocierre.
      snapDeVertices: true,
    });
    this._refreshDisponibilidad();
    this._state.addOnChangeListener(this._refreshDisponibilidad);
  }

  dispose(): void {
    if (this._relojCorte !== null) clearTimeout(this._relojCorte);
    this._relojCorte = null;
    this._quitarContornoADividir();
    this._engine?.destruir();
    this._engine = null;
    this._engineDibujo?.destruir();
    this._engineDibujo = null;
    if (this._splitBtn) L.DomEvent.off(this._splitBtn, "click", this._onSplitClick);
    if (this._unirBtn) L.DomEvent.off(this._unirBtn, "click", this._onUnirClick);
    if (this._editarBtn) {
      L.DomEvent.off(this._editarBtn, "click", this._onEditarClick);
    }
    if (this._borrarBtn) {
      L.DomEvent.off(this._borrarBtn, "click", this._onBorrarClick);
    }
    if (this._dibujarBtn) {
      L.DomEvent.off(this._dibujarBtn, "click", this._onDibujarClick);
    }
    this._dibujarBtn = null;
    this._splitBtn = null;
    this._unirBtn = null;
    this._editarBtn = null;
    this._borrarBtn = null;
  }

  /** Notifica qué herramienta quedó activa (o `null`). Lo usa
   *  `LoteSplitMapRender` para mostrar el aviso y para habilitar la selección
   *  de lotes del modo Unir. */
  setOnHerramientaChange(
    handler: (herramienta: LoteSplitHerramienta | null) => void,
  ): void {
    this._onHerramienta = handler;
  }

  /** Pide confirmación y borra el lote. Lo llama `LoteSplitMapRender` cuando
   *  el usuario lo eligió en el modo Borrar. */
  confirmarBorrado(id: number, onCerrado: () => void): void {
    const entry = this._state.getAll().find((e) => e.id === id);
    if (!entry) {
      onCerrado();
      return;
    }

    new ConfirmModalRender({
      title: "Borrar lote",
      message: `¿Borrar el lote "${entry.nombreLote}"? El área que ocupa deja de ser parte del predio.`,
      confirmLabel: "Borrar",
      onConfirm: async () => {
        if (!this._state.eliminarLote(id)) {
          serviceProvider
            .getErrorService()
            .addError(
              "No se pudo borrar",
              "El predio tiene que quedar con al menos un lote.",
            );
        }
        onCerrado();
      },
      onCancel: onCerrado,
    }).init();
  }

  /** Valida la adyacencia, pide confirmación y une. Lo llama
   *  `LoteSplitMapRender` cuando el usuario eligió las dos piezas. */
  /** Pide confirmación y manda la unión al servidor.
   *
   *  **Acá no se valida la adyacencia.** La decide el servidor, que es el que
   *  tiene la geometría guardada: antes se medía el borde compartido con turf
   *  y, si pasaba, el navegador unía las piezas y mandaba el resultado. El
   *  problema no era la medición sino quién la hacía — el servidor no tenía
   *  forma de verificar ni la adyacencia ni la pieza unida.
   *
   *  Por lo mismo el rechazo ya no se arma acá: si no son vecinos, el servidor
   *  contesta `LOTES_NO_ADYACENTES` con un mensaje que nombra los dos lotes, y
   *  lo muestra quien atiende la operación (`RegistroFincaPaso4Render`).
   *
   *  Los dos lotes quedan en amarillo mientras el modal espera, que es lo que ya
   *  hacía `LoteSplitMapRender._elegirParaUnir`. */
  confirmarUnion(idA: number, idB: number, onCerrado: () => void): void {
    const tempIdA = this._state.tempIdDe(idA);
    const tempIdB = this._state.tempIdDe(idB);
    if (!tempIdA || !tempIdB) {
      // Un lote cuya alta todavía no volvió del servidor no tiene id con el que
      // nombrarlo. Pasa sólo si el usuario une una pieza recién cortada antes de
      // que su guardado vuelva.
      onCerrado();
      return;
    }

    new ConfirmModalRender({
      title: "Unir lotes",
      message: "¿Unir estos dos lotes? Se convertirán en un solo lote.",
      confirmLabel: "Unir",
      onConfirm: async () => {
        this._onUnir?.(tempIdA, tempIdB);
        onCerrado();
      },
      onCancel: onCerrado,
    }).init();
  }

  /** Notifica que hay una unión que mandar al servidor. Lo atiende quien tiene
   *  el registro y el servicio gRPC; esta toolbar no habla con el backend —
   *  mismo reparto que `setOnDividir`. */
  setOnUnir(handler: (loteTempIdA: string, loteTempIdB: string) => void): void {
    this._onUnir = handler;
  }

  private _onUnir:
    | ((loteTempIdA: string, loteTempIdB: string) => void)
    | null = null;

  private _state!: LoteSplitState;
  private _engine: LoteSplitLineEngine | null = null;
  private _splitBtn: HTMLButtonElement | null = null;
  private _unirBtn: HTMLButtonElement | null = null;
  private _editarBtn: HTMLButtonElement | null = null;
  private _borrarBtn: HTMLButtonElement | null = null;
  private _dibujarBtn: HTMLButtonElement | null = null;
  private _engineDibujo: AoiDrawEngine | null = null;
  private _activa: LoteSplitHerramienta | null = null;
  private _onHerramienta:
    | ((herramienta: LoteSplitHerramienta | null) => void)
    | null = null;

  private _onSplitClick = (): void => this._toggle("dividir");
  private _onUnirClick = (): void => this._toggle("unir");
  private _onEditarClick = (): void => this._toggle("editar");
  private _onBorrarClick = (): void => this._toggle("borrar");
  private _onDibujarClick = (): void => this._toggle("dibujar");

  /** Terminó el trazo del modo Dibujar: se manda al servidor, que es el que
   *  arma el lote.
   *
   *  **Acá ya no se valida ni se agrega nada al estado.** Antes esta función
   *  sacaba la pieza del motor y la metía con `LoteSplitState.agregarLote`, que
   *  era la última validación geométrica que corría en el navegador (forma
   *  simple, solape contra los otros lotes). Ahora viajan los PUNTOS y el
   *  servidor decide: mismo criterio que el corte, la unión y los vértices.
   *
   *  El trazo puede venir ABIERTO, y eso es deliberado: si empezó y terminó
   *  sobre vértices del polígono padre, el servidor completa el contorno con el
   *  borde que ya tiene guardado (ver `AoiDrawEngine.setVerticesDeAutocierre`).
   *  Al cliente le da igual cuál de las dos formas es — las dos son el mismo
   *  rpc. */
  private _onTrazoDeLote = (puntos: Position[]): void => {
    if (puntos.length < 2) return;
    this._onDibujado?.(puntos);
  };

  /** El trazo tocó un vértice conocido: se le pregunta al SERVIDOR si con eso
   *  se puede cerrar un lote válido y, sólo si se puede, se confirma con el
   *  usuario.
   *
   *  **Qué arregla.** Antes el motor cerraba el trazo apenas tocaba dos
   *  vértices conocidos y lo mandaba a crear. Bastaba apoyar dos puntos
   *  seguidos sobre vértices de lotes que no son vecinos para que el contorno
   *  que los une no existiera, o para que lo que cerraba pisara otro lote: el
   *  trazo entero se perdía y salía un error ("los lotes se solapan", "el trazo
   *  quedó abierto"). Ahora, si no cierra, no pasa absolutamente nada y el
   *  usuario sigue dibujando.
   *
   *  **Un rechazo no se le muestra.** Mientras se dibuja, un trazo que todavía
   *  no cierra es un estado normal: avisarlo en cada vértice sería ruido
   *  constante. El motivo que devuelve el servidor queda disponible para
   *  diagnóstico, no para la pantalla.
   *
   *  Mientras la consulta viaja no se dispara otra (`_consultandoAutocierre`):
   *  el usuario puede poner varios vértices seguidos sobre el borde del vecino
   *  —es lo normal al dibujar apoyado— y serían otras tantas preguntas
   *  encimadas. */
  private _onAutocierrePosible = (puntos: Position[]): void => {
    const consultar = this._onValidarAutocierre;
    if (!consultar || this._consultandoAutocierre) return;
    this._consultandoAutocierre = true;

    void consultar(puntos)
      .then((resultado) => {
        this._consultandoAutocierre = false;
        if (!resultado.valido) return;
        // Entre la pregunta y la respuesta el usuario pudo cerrar la forma a
        // mano, cancelar el trazo o cambiar de herramienta.
        if (this._activa !== "dibujar" || !this._engineDibujo?.hayTrazo) return;
        this._confirmarAutocierre(resultado.areaHas);
      })
      .catch(() => {
        // Sin respuesta no se autocierra, y el usuario sigue dibujando. No se
        // avisa por lo mismo que un rechazo: no es un error suyo.
        this._consultandoAutocierre = false;
      });
  };

  /** Pregunta si cerrar el lote contra el borde existente. Aceptar lo crea;
   *  cancelar deja el trazo como está, para seguir dibujándolo a mano. */
  private _confirmarAutocierre(areaHas: number): void {
    new ConfirmModalRender({
      title: "Autocerrar lote",
      message:
        `El trazo se puede cerrar siguiendo el borde que ya existe, y el lote quedaría de ` +
        `${areaHas.toFixed(2)} ha. ¿Cerrarlo así? Si prefiere, cancele y siga dibujando el contorno.`,
      confirmLabel: "Cerrar lote",
      onConfirm: async () => {
        // Se revalida el estado: el modal es asincrónico y entre medio el trazo
        // pudo desaparecer.
        if (this._activa !== "dibujar" || !this._engineDibujo?.hayTrazo) return;
        this._engineDibujo.terminarTrazoAbierto();
      },
    }).init();
  }

  /** Quién responde si un trazo se puede autocerrar. Lo pone el paso 4, que es
   *  el que tiene el registro y el servicio gRPC. */
  setOnValidarAutocierre(
    handler: (
      puntos: Position[],
    ) => Promise<{ valido: boolean; areaHas: number }>,
  ): void {
    this._onValidarAutocierre = handler;
  }

  private _onValidarAutocierre:
    | ((puntos: Position[]) => Promise<{ valido: boolean; areaHas: number }>)
    | null = null;
  private _consultandoAutocierre = false;

  /** Notifica que hay un lote dibujado que mandar al servidor. Lo atiende quien
   *  tiene el registro y el servicio gRPC (`RegistroFincaPaso4Render`). */
  setOnDibujado(handler: (trazo: Position[]) => void): void {
    this._onDibujado = handler;
  }

  private _onDibujado: ((trazo: Position[]) => void) | null = null;

  /** Los anillos contra los que pega el snap del dibujo: los de los lotes Y los
   *  de los polígonos padre.
   *
   *  Los del padre hacen falta aunque sus vértices sean, casi siempre, vértices
   *  de algún lote: el borde EXTERIOR del predio no siempre tiene un lote a los
   *  dos lados, y es justo ahí donde arranca un lote nuevo que se apoya en el
   *  perímetro. */
  /** Contra qué se pega el trazo del modo Dibujar, y sobre qué vértices puede
   *  autocerrarse: los anillos de los lotes **y los de los polígonos padre**.
   *
   *  **Los del padre estuvieron afuera entre el 2026-10-09 y el 2026-10-23.**
   *  Se habían quitado por costo: calcularlos exigía unir todos los lotes con
   *  turf (`fx_poligonosPadreDe`) y eso congelaba la app un par de segundos
   *  cada vez, al activar la herramienta y después de cada lote dibujado. El
   *  argumento para que no se perdiera nada era que el borde de un padre es la
   *  unión de los bordes de sus lotes, así que cada vértice suyo ya estaba en
   *  el anillo de algún lote.
   *
   *  Ese argumento vale sólo mientras cada pieza esté enteramente cubierta por
   *  lotes. Deja de valer apenas se borra uno: ahí queda un hueco cuyo borde
   *  exterior es del padre y de ningún lote — y es exactamente donde el
   *  usuario va a dibujar el lote que lo reemplaza, apoyándose en el perímetro.
   *
   *  Ahora no hay que calcularlos: los trae el paso ya hechos, del servidor,
   *  que es quien los mantiene al día (`setPoligonosPadre`). El costo que
   *  motivó sacarlos no existe. */
  private _anillosDeSnapDelDibujo(): L.LatLng[][] {
    return [...this._bordesDeLasPiezas(), ...this._bordesDelPadre()];
  }

  /** Los anillos de los polígonos padre, en coordenadas de Leaflet. Salen de
   *  la geometría que guardó `setPoligonosPadre`: memoria, sin renderizar. */
  private _bordesDelPadre(): L.LatLng[][] {
    const bordes: L.LatLng[][] = [];
    this._poligonosPadre.forEach((polygon) => {
      polygon.coordinates.forEach((ring) => {
        bordes.push(ring.map((c) => L.latLng(c[1], c[0])));
      });
    });
    return bordes;
  }

  /** Guarda los polígonos padre del registro y rehace las guías del dibujo con
   *  ellos. Lo llama `LoteSplitMapRender.setPoligonosPadre`; puede llegar antes
   *  o después de `init()`, de ahí que `_refrescarGuiasDelDibujo` tolere que el
   *  motor todavía no exista. */
  setPoligonosPadre(poligonos: readonly Polygon[]): void {
    this._poligonosPadre = poligonos;
    this._refrescarGuiasDelDibujo();
  }

  private _poligonosPadre: readonly Polygon[] = [];

  private _refrescarGuiasDelDibujo(): void {
    // Sin motor todavía no corrió `init()`, y entonces `_state` tampoco está
    // asignado: `_anillosDeSnapDelDibujo` tiraría al leerlo. Pasa cuando los
    // polígonos padre llegan antes que el mapa (`setPoligonosPadre`); no hay
    // nada que refrescar, y el `init()` arma las guías con lo ya guardado.
    if (!this._engineDibujo) return;
    const anillos = this._anillosDeSnapDelDibujo();
    this._engineDibujo.setBordesDeSnap(anillos);
    this._engineDibujo.setVerticesDeAutocierre(anillos.flat());
  }

  /** Unir no tiene sentido con menos de 2 piezas. Corre en cada cambio del
   *  estado, así que aprovecha para rehacer la guía de snap del trazo: como
   *  la herramienta ya no se apaga sola, tras un corte o una unión las piezas
   *  son otras y la guía armada al activarla quedó vieja. */
  private _refreshDisponibilidad = (): void => {
    // El estado cambió: si había un corte en vuelo, sus lotes ya llegaron.
    if (this._corteEnVuelo) {
      this._corteEnVuelo = false;
      if (this._relojCorte !== null) clearTimeout(this._relojCorte);
      this._relojCorte = null;
    }

    const total = this._state.getAll().length;
    if (this._unirBtn) this._unirBtn.disabled = total < 2;
    // Borrar necesita lo mismo que Unir: con un solo lote no hay nada que
    // sacar — un registro sin lotes no se puede finalizar (ver
    // `LoteSplitState.eliminarLote`).
    if (this._borrarBtn) this._borrarBtn.disabled = total < 2;
    if (total < 2 && (this._activa === "unir" || this._activa === "borrar")) {
      this._setActiva(null);
    }
    if (this._activa === "dividir") {
      this._engine?.refrescarBordes(this._bordesDeLasPiezas());
    }
    // Lo mismo para Dibujar: después de cortar, unir o dibujar otro lote, los
    // bordes contra los que pegar —y los vértices del padre— son otros.
    if (this._activa === "dibujar") {
      this._refrescarGuiasDelDibujo();
    }
  };

  /** Se cerró la línea de corte: se manda al servidor junto con el lote que el
   *  usuario había tocado.
   *
   *  **El corte ya no se hace acá.** Antes esta línea llamaba a
   *  `LoteSplitState.splitAt`, que partía el polígono con turf en el navegador
   *  y dejaba que `LotesTempSync` mandara las dos mitades. Ahora se manda la
   *  OPERACIÓN y el servidor aplica las mismas reglas (ver
   *  `LoteSplitGeometria`): el resultado del corte es lo que se guarda, y eso
   *  lo decide el servidor.
   *
   *  La herramienta QUEDA ACTIVA al terminar un corte (pedido explícito), pero
   *  vuelve a esperar un lote: cada corte se aplica sobre uno, así que para el
   *  siguiente hay que volver a elegirlo. */
  private _onLineaTrazada = (lineCoords: number[][]): void => {
    const loteId = this._loteADividir;
    this._soltarLoteADividir();

    if (lineCoords.length < 2 || !loteId) return;
    this._marcarCorteEnVuelo();
    this._onDividir?.(loteId, lineCoords as Position[]);
  };

  /** Desde que el corte sale hacia el servidor hasta que vuelven los lotes, la
   *  herramienta NO acepta otro lote para dividir.
   *
   *  **Qué bug arregla.** El trazo se cierra con el `mouseup` (por proximidad
   *  al último vértice), y Leaflet dispara el `click` del mapa de ese MISMO
   *  gesto un instante después. Para entonces `_onLineaTrazada` ya soltó el
   *  lote, así que ese click encontraba la herramienta otra vez "esperando" y
   *  empezaba un corte nuevo sobre el lote de abajo — que es el original, el
   *  que el servidor justo está partiendo. Se veía como el lote quedando en
   *  aguamarina y el trazo habilitado sobre algo que ya no existe, mientras en
   *  el fondo la subdivisión entraba bien. Intermitente porque cerrar el trazo
   *  clickeando el marcador del último vértice NO lo provoca: ese click sí
   *  corta la propagación.
   *
   *  Lo levanta el cambio de estado que traen los lotes nuevos
   *  (`_refreshDisponibilidad`). El reloj es la red de seguridad para el caso
   *  en que el corte falle: ahí el error se avisa y el estado NO cambia, así
   *  que sin esto la herramienta quedaría trabada hasta cambiar de modo. */
  private _marcarCorteEnVuelo(): void {
    this._corteEnVuelo = true;
    if (this._relojCorte !== null) clearTimeout(this._relojCorte);
    this._relojCorte = setTimeout(() => {
      this._relojCorte = null;
      this._corteEnVuelo = false;
    }, ESPERA_MAXIMA_CORTE_MS);
  }

  private _corteEnVuelo = false;
  private _relojCorte: ReturnType<typeof setTimeout> | null = null;

  /** Notifica que hay un corte que mandar al servidor. Lo atiende quien tiene
   *  el registro y el servicio gRPC (`RegistroFincaPaso4Render`); esta toolbar
   *  no habla con el backend. */
  setOnDividir(
    handler: (loteTempId: string, linea: Position[]) => void,
  ): void {
    this._onDividir = handler;
  }

  /** ¿El modo Dividir está esperando que el usuario elija un lote?
   *
   *  Lo pregunta `LoteSplitMapRender` para saber de quién es el click: mientras
   *  espera, es suyo (elige el lote); una vez elegido, los clicks son del trazo
   *  y el motor de la línea los toma por su cuenta. */
  esperandoLoteParaDividir(): boolean {
    return (
      this._activa === "dividir" &&
      this._loteADividir === null &&
      // Con un corte en vuelo no hay nada que elegir: los lotes de ahora mismo
      // pueden dejar de existir en cuanto conteste el servidor (ver
      // `_marcarCorteEnVuelo`).
      !this._corteEnVuelo
    );
  }

  /** El usuario tocó un lote en modo Dividir: con su geometría se arma el snap
   *  del trazo.
   *
   *  Los bordes del snap salen de ESTE lote y no de todas las piezas: el corte
   *  se va a aplicar sobre él, así que lo que tiene que quedar exacto son SUS
   *  vértices.
   *
   *  **La geometría sale de la copia del estado, no de la base** (2026-10-27,
   *  pedido del usuario). Antes se leía por gRPC (`LotesWfsService` →
   *  `LeerLoteTemporal`): un viaje más cada vez que se tocaba un lote, 0,62 s
   *  medido en pruebas. La copia del estado YA es la de la base: cada operación
   *  del paso reemplaza el estado con TODOS los lotes que devuelve el servidor
   *  (`RegistroFincaPaso4Render._lotesDeLaBase`), y al entrar se leen de la base.
   *  El estado los normaliza (`LoteSplitState._normalizar`: 7 decimales, que es
   *  como se guardan, y sin vértices repetidos seguidos), así que las posiciones
   *  contra las que se pega la línea son las mismas. El modo Editar sí sigue
   *  leyendo de la base (`LoteSplitMapRender._editarVerticesDeAsync`): ahí la
   *  inserción nombra el tramo por su número en el anillo, y un repetido que la
   *  copia no tiene correría la cuenta.
   *
   *  **Para revertir:** volver a leer antes de armar el snap —
   *  `const leida = await new LotesWfsService().leerAsync(registroId, tempId);`,
   *  después comprobar de nuevo que Dividir siga activa (la lectura viaja) y usar
   *  `leida ?? <copia del estado>`—, y volver a pasarle el `registroId` a `init`
   *  desde `LoteSplitMapRender`.
   *
   *  No se renderiza nada más que el contorno del lote elegido. */
  async comenzarCorteDeAsync(loteId: number): Promise<void> {
    if (this._activa !== "dividir") return;

    const tempId = this._state.tempIdDe(loteId);
    // Sin id del servidor no hay a quién mandarle el corte: es un lote cuyo
    // alta todavía no volvió.
    if (!tempId) return;

    const geometria = this._state.getAll().find((e) => e.id === loteId)?.polygon;
    if (!geometria) return;

    this._loteADividir = tempId;

    const anillos = geometria.coordinates.map((anillo) =>
      anillo.map((c) => L.latLng(c[1], c[0])),
    );
    this._pintarContornoADividir(anillos);
    this._engine?.activar(anillos);
    this._onHerramienta?.(this._activa);
  }

  /** Vuelve a esperar un lote: se terminó (o se abandonó) un corte. */
  private _soltarLoteADividir(): void {
    if (this._loteADividir === null) return;
    this._loteADividir = null;
    this._quitarContornoADividir();
    this._engine?.desactivar();
    this._onHerramienta?.(this._activa);
  }

  /** Marca en aguamarina el lote elegido para dividir. Ver
   *  `CONTORNO_A_DIVIDIR`. */
  private _pintarContornoADividir(anillos: L.LatLng[][]): void {
    this._quitarContornoADividir();
    if (!this._mapa) return;
    this._contornoADividir = L.polygon(anillos, CONTORNO_A_DIVIDIR).addTo(
      this._mapa,
    );
  }

  private _quitarContornoADividir(): void {
    if (!this._contornoADividir || !this._mapa) return;
    this._mapa.removeLayer(this._contornoADividir);
    this._contornoADividir = null;
  }

  private _contornoADividir: L.Polygon | null = null;
  private _mapa: L.Map | null = null;

  /** El lote que se está dividiendo (su id del servidor), o `null` si Dividir
   *  todavía está esperando que el usuario elija uno. */
  private _loteADividir: string | null = null;
  private _onDividir:
    | ((loteTempId: string, linea: Position[]) => void)
    | null = null;

  private _toggle(herramienta: LoteSplitHerramienta): void {
    if (this._activa === herramienta) {
      this._setActiva(null);
      return;
    }
    if (
      (herramienta === "unir" || herramienta === "borrar") &&
      this._state.getAll().length < 2
    ) {
      return;
    }
    this._setActiva(herramienta);
  }

  private _setActiva(herramienta: LoteSplitHerramienta | null): void {
    this._activa = herramienta;

    // Dividir ya NO enciende el trazo al activarse: primero hay que tocar un
    // lote (ver `comenzarCorteDeAsync`). Encenderlo acá hacía que el primer
    // click pusiera un vértice en vez de elegir el lote.
    if (herramienta !== "dividir") {
      this._loteADividir = null;
      // El contorno del lote a dividir se va con la herramienta: apagar Dividir
      // sin esto dejaba el aguamarina dibujado sobre un lote que ya no se está
      // cortando.
      this._quitarContornoADividir();
      this._engine?.desactivar();
    }

    if (herramienta === "dibujar") {
      this._refrescarGuiasDelDibujo();
      this._engineDibujo?.activar("draw");
    } else {
      this._engineDibujo?.desactivar();
    }

    this._splitBtn?.classList.toggle(
      "sx-map-btn--active",
      herramienta === "dividir",
    );
    this._unirBtn?.classList.toggle("sx-map-btn--active", herramienta === "unir");
    this._editarBtn?.classList.toggle(
      "sx-map-btn--active",
      herramienta === "editar",
    );
    this._borrarBtn?.classList.toggle(
      "sx-map-btn--active",
      herramienta === "borrar",
    );
    this._dibujarBtn?.classList.toggle(
      "sx-map-btn--active",
      herramienta === "dibujar",
    );
    this._onHerramienta?.(herramienta);
  }

  /** Anillos de las piezas actuales, para el snap del trazo. */
  private _bordesDeLasPiezas(): L.LatLng[][] {
    const bordes: L.LatLng[][] = [];
    this._state.getAll().forEach((entry) => {
      entry.polygon.coordinates.forEach((ring) => {
        bordes.push(ring.map((c) => L.latLng(c[1], c[0])));
      });
    });
    return bordes;
  }

  private _makeButton(
    icon: string,
    title: string,
    onClick: () => void,
  ): HTMLButtonElement {
    const btn = L.DomUtil.create(
      "button",
      "sx-map-btn sx-map-btn--lg",
    ) as HTMLButtonElement;
    btn.innerHTML = `<i class="ph ${icon}"></i><span>${title}</span>`;
    btn.title = title;
    L.DomEvent.disableClickPropagation(btn);
    L.DomEvent.on(btn, "click", onClick);
    return btn;
  }
}
